import type { ClientMsg, ServerMsg } from '../shared/protocol.ts';

export type NetState = 'connecting' | 'open' | 'closed';

/** WebSocket with automatic reconnect + backoff. */
export class Net {
  private ws: WebSocket | null = null;
  private backoff = 500;
  private stopped = false;
  private readonly onMessage: (msg: ServerMsg) => void;
  private readonly onState: (s: NetState) => void;

  constructor(onMessage: (msg: ServerMsg) => void, onState: (s: NetState) => void) {
    this.onMessage = onMessage;
    this.onState = onState;
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && !this.ws && !this.stopped) this.connect();
    });
  }

  connect(): void {
    if (this.ws || this.stopped) return;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws = ws;
    this.onState('connecting');
    ws.onopen = () => {
      this.backoff = 500;
      this.onState('open');
    };
    ws.onmessage = (e) => {
      try {
        this.onMessage(JSON.parse(e.data));
      } catch (err) {
        console.error(err);
      }
    };
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      this.onState('closed');
      if (this.stopped) return;
      setTimeout(() => this.connect(), this.backoff);
      this.backoff = Math.min(this.backoff * 2, 8000);
    };
  }

  send(msg: ClientMsg): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  stop(): void {
    this.stopped = true;
    this.ws?.close();
  }
}
